/**
 * One tab owns the device database (spec 7: "Single-tab: a Web Lock plus
 * pauseVfs()/unpauseVfs() hand-over"). The opfs-sahpool VFS keeps every file handle
 * open, so a second tab cannot install it; instead of failing, the second tab shows a
 * hand-over screen (section 04 renders `DbLockState`) and can ask for the database.
 *
 * Holder and requester talk over a BroadcastChannel:
 *   requester: 'minta'  -> holder pauses its worker (db:pause), releases the Web Lock,
 *                          shows 'dipindahkan' and answers 'dilepas'
 *   requester: acquires the lock (or, without Web Locks, sees 'dilepas'), resumes or
 *              spawns its worker and shows 'memegang'
 *   'ping' / 'pong' is the best-effort guard when Web Locks are unavailable.
 *
 * Never `steal: true`: stealing the lock does not release the other worker's handles.
 * Platform-neutral: the lock manager, channel and timers are injectable for tests.
 */

export type DbLockState =
  | { status: 'memulai' }
  | { status: 'memegang' }
  | { status: 'dipegang_tab_lain' }
  | { status: 'meminta' }
  | { status: 'dipindahkan' }

export interface LockManagerLike {
  request(
    name: string,
    options: { ifAvailable?: boolean; mode?: 'exclusive' | 'shared' },
    callback: (lock: object | null) => unknown,
  ): Promise<unknown>
}

export interface ChannelLike {
  postMessage(message: unknown): void
  addEventListener(type: 'message', listener: (event: { data: unknown }) => void): void
  removeEventListener(type: 'message', listener: (event: { data: unknown }) => void): void
  close(): void
}

export interface Timers {
  setTimeout(fn: () => void, ms: number): unknown
  clearTimeout(handle: unknown): void
}

export interface DbLockOptions {
  /** Lock and channel name; one per origin. */
  name?: string
  /** Defaults to `navigator.locks`; null forces the BroadcastChannel-only fallback. */
  locks?: LockManagerLike | null
  /** Defaults to `new BroadcastChannel(name)`; null disables cross-tab messages. */
  channel?: ChannelLike | null
  timers?: Timers
  onState: (state: DbLockState) => void
  /** Close the worker's database and release its OPFS handles (db:pause). */
  onPause: () => Promise<void>
  /** Re-open after the lock arrives (db:resume, or spawn the worker). */
  onResume: () => Promise<void>
  /** How long to wait for a 'pong' before assuming no other tab (fallback only). */
  pingTimeoutMs?: number
  /** A second `ifAvailable` attempt after this delay covers a tab that is just closing. */
  retryMs?: number
  /** Fallback only: give up waiting for 'dilepas' after this long. */
  handoverTimeoutMs?: number
}

// Legacy coordination name from SIMU: an old tab and a new tab hand the database over through
// it during an update, so it is kept (pinned: test/nama-warisan.test.ts).
type LockMessage = { simuDbLock: 'ping' | 'pong' | 'minta' | 'dilepas' }

function isLockMessage(data: unknown): data is LockMessage {
  return (
    typeof data === 'object' &&
    data !== null &&
    'simuDbLock' in data &&
    typeof (data as LockMessage).simuDbLock === 'string'
  )
}

// Legacy coordination name from SIMU (the Web Lock and the BroadcastChannel): an old tab and a
// new tab coordinate through it during an update, so it is kept (pinned: test/nama-warisan.test.ts).
export const DB_LOCK_NAME = 'simu-db'

function defaultLocks(): LockManagerLike | null {
  const nav = (globalThis as { navigator?: { locks?: LockManagerLike } }).navigator
  return nav?.locks ?? null
}

function defaultChannel(name: string): ChannelLike | null {
  const ctor = (globalThis as unknown as { BroadcastChannel?: new (name: string) => ChannelLike })
    .BroadcastChannel
  return ctor === undefined ? null : new ctor(name)
}

const defaultTimers: Timers = {
  setTimeout: (fn, ms) => setTimeout(fn, ms),
  clearTimeout: (handle) => clearTimeout(handle as ReturnType<typeof setTimeout>),
}

export class DbLock {
  state: DbLockState = { status: 'memulai' }
  private holding = false
  private releaseLock: (() => void) | null = null
  private readonly name: string
  private readonly locks: LockManagerLike | null
  private readonly channel: ChannelLike | null
  private readonly timers: Timers
  private readonly options: DbLockOptions
  private waiters = new Map<'pong' | 'dilepas', Set<() => void>>()
  private readonly listener = (event: { data: unknown }) => {
    void this.handle(event.data)
  }

  constructor(options: DbLockOptions) {
    this.options = options
    this.name = options.name ?? DB_LOCK_NAME
    this.locks = options.locks === undefined ? defaultLocks() : options.locks
    this.channel = options.channel === undefined ? defaultChannel(this.name) : options.channel
    this.timers = options.timers ?? defaultTimers
    this.channel?.addEventListener('message', this.listener)
  }

  get isHolding(): boolean {
    return this.holding
  }

  /** Try to take the database for this tab. False means another tab has it. */
  async acquire(): Promise<boolean> {
    if (await this.tryAcquire()) return this.hold()
    await this.sleep(this.options.retryMs ?? 200)
    if (await this.tryAcquire()) return this.hold()
    this.setState({ status: 'dipegang_tab_lain' })
    return false
  }

  /** Ask the holder for the database; resolves true once this tab holds it. */
  async requestHandover(): Promise<boolean> {
    if (this.holding) return true
    this.setState({ status: 'meminta' })
    const released = this.waitFor('dilepas', this.options.handoverTimeoutMs ?? 10_000)
    this.channel?.postMessage({ simuDbLock: 'minta' } satisfies LockMessage)
    let got: boolean
    if (this.locks !== null) {
      got = await this.requestLock({})
    } else {
      got = await released
    }
    if (!got) {
      this.setState({ status: 'dipegang_tab_lain' })
      return false
    }
    this.holding = true
    this.setState({ status: 'memegang' })
    await this.options.onResume()
    return true
  }

  /** Give the lock up without a hand-over (tab closing). */
  release(): void {
    this.holding = false
    const release = this.releaseLock
    this.releaseLock = null
    release?.()
  }

  close(): void {
    this.release()
    this.channel?.removeEventListener('message', this.listener)
    this.channel?.close()
  }

  private hold(): true {
    this.holding = true
    this.setState({ status: 'memegang' })
    return true
  }

  private setState(state: DbLockState): void {
    this.state = state
    this.options.onState(state)
  }

  private async tryAcquire(): Promise<boolean> {
    if (this.locks !== null) return this.requestLock({ ifAvailable: true })
    if (this.channel === null) return true
    return !(await this.anyoneElse())
  }

  /** Resolves with whether the lock was granted; the lock is held until `release`. */
  private requestLock(options: { ifAvailable?: boolean }): Promise<boolean> {
    const locks = this.locks
    if (locks === null) return Promise.resolve(false)
    return new Promise<boolean>((resolveGranted) => {
      locks
        .request(this.name, { ...options, mode: 'exclusive' }, (lock) => {
          if (lock === null) {
            resolveGranted(false)
            return undefined
          }
          return new Promise<void>((release) => {
            this.releaseLock = release
            resolveGranted(true)
          })
        })
        .catch(() => resolveGranted(false))
    })
  }

  private async anyoneElse(): Promise<boolean> {
    const pong = this.waitFor('pong', this.options.pingTimeoutMs ?? 250)
    this.channel?.postMessage({ simuDbLock: 'ping' } satisfies LockMessage)
    return pong
  }

  private waitFor(kind: 'pong' | 'dilepas', timeoutMs: number): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      const set = this.waiters.get(kind) ?? new Set<() => void>()
      this.waiters.set(kind, set)
      const timer = this.timers.setTimeout(() => {
        set.delete(done)
        resolve(false)
      }, timeoutMs)
      const done = () => {
        this.timers.clearTimeout(timer)
        set.delete(done)
        resolve(true)
      }
      set.add(done)
    })
  }

  private notify(kind: 'pong' | 'dilepas'): void {
    for (const done of [...(this.waiters.get(kind) ?? [])]) done()
  }

  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => {
      this.timers.setTimeout(resolve, ms)
    })
  }

  private async handle(data: unknown): Promise<void> {
    if (!isLockMessage(data)) return
    switch (data.simuDbLock) {
      case 'ping':
        if (this.holding) this.channel?.postMessage({ simuDbLock: 'pong' } satisfies LockMessage)
        return
      case 'pong':
        this.notify('pong')
        return
      case 'dilepas':
        this.notify('dilepas')
        return
      case 'minta':
        if (this.holding) await this.handOver()
        return
    }
  }

  private async handOver(): Promise<void> {
    this.holding = false
    try {
      await this.options.onPause()
    } catch {
      this.holding = true
      return
    }
    const release = this.releaseLock
    this.releaseLock = null
    release?.()
    this.setState({ status: 'dipindahkan' })
    this.channel?.postMessage({ simuDbLock: 'dilepas' } satisfies LockMessage)
  }
}
